/**
 * Network-dashboard APIs — the rich landing-page tile vitals (LANDING).
 *
 *   GET /networks/dashboard                  → { networks: [ <networkVitals> ] }
 *   GET /networks/{id}/rooms-dashboard?offset=&limit=
 *                                            → { networkId, rooms: [ <roomVitals> ],
 *                                                total, offset, limit, hasMore }
 *
 * networkVitals: { networkId, name, status, roomCount, agentCount, totalAgents,
 *   activeRoomCount, runsTotal, runsSucceeded, successRatePct: number|null,
 *   consumedPoints, budgetPoints?: number, outcomePct?: number }
 * roomVitals: { poolId, name, lifecycleStatus, stepflowVersion, agentCount,
 *   isActive, runsTotal, runsSucceeded, successRatePct, consumedPoints,
 *   outcomePct: number|null, lastActivityAt }
 *
 * Both are cheap server-side aggregates (see backend dashboard_impl.py). Mirrors
 * the ``authedFetch`` convention used across the ui-shared service clients.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let data = null;
    try { data = await res.json(); } catch { /* non-JSON error */ }
    const err = new Error((data && data.error) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return res.json();
}

/** Vitals for every network the caller can see (the tile grid). */
export async function getNetworksDashboard() {
  const data = await authedFetch('/networks/dashboard');
  return { networks: Array.isArray(data?.networks) ? data.networks : [] };
}

/** Per-room vitals for one network (the inline expansion), paginated (default 9). */
export async function getRoomsDashboard(networkId, { offset = 0, limit = 9 } = {}) {
  const nid = encodeURIComponent(String(networkId || '').trim());
  const sp = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  const data = await authedFetch(`/networks/${nid}/rooms-dashboard?${sp}`);
  return {
    networkId: data?.networkId || networkId,
    networkName: data?.networkName || '',
    rooms: Array.isArray(data?.rooms) ? data.rooms : [],
    total: Number(data?.total || 0),
    offset: Number(data?.offset || 0),
    limit: Number(data?.limit || limit),
    hasMore: Boolean(data?.hasMore),
  };
}

/**
 * Vitals for a set of rooms the caller is a member of — the "My Rooms" landing.
 *
 * The caller's rooms span MANY networks (from ``GET /my/agentic-networks``), but the
 * per-room vitals aggregate is network-scoped (``/networks/{id}/rooms-dashboard``).
 * So we fan out ONE rooms-dashboard call per distinct network the caller has a room
 * in, then keep only the pools they're actually a member of, merging the membership
 * row (role, displayName, lifecycle) with the vitals row (runsTotal = "N executions",
 * success, consumption, isActive). A room-only, non-network member is authorized for
 * this endpoint (tenant-scoped gate), so this works for the pure-room persona.
 *
 * ``rooms`` in = the membership rows: ``[{ networkId, poolId, displayName, role,
 *   lifecycleStatus, lastActivityAt, releaseVersion }]``.
 * Returns the same rows enriched with ``{ runsTotal, runsSucceeded, successRatePct,
 *   consumedPoints, consumedUsd, outcomePct, isActive, agentCount, networkName }``.
 * A network whose fan-out fails degrades to the bare membership row (no vitals) so
 * one bad network never blanks the whole landing.
 *
 * ``limit`` caps the per-network page (default 50 covers virtually all rooms in a
 * single network without a second round-trip).
 */
export async function getMyRoomsVitals(rooms, { limit = 50 } = {}) {
  const list = Array.isArray(rooms) ? rooms.filter((r) => r && r.networkId && r.poolId) : [];
  if (!list.length) return [];

  // One rooms-dashboard call per DISTINCT network (dedup the fan-out).
  const byNetwork = new Map();
  for (const r of list) {
    if (!byNetwork.has(r.networkId)) byNetwork.set(r.networkId, []);
    byNetwork.get(r.networkId).push(r);
  }

  const results = await Promise.all(
    Array.from(byNetwork.keys()).map(async (networkId) => {
      try {
        const { rooms: vitalsRooms, networkName } = await getRoomsDashboard(networkId, { offset: 0, limit });
        const byPool = new Map(vitalsRooms.map((v) => [v.poolId, v]));
        return { networkId, networkName, byPool };
      } catch {
        // Degrade gracefully — this network contributes bare membership rows.
        return { networkId, networkName: '', byPool: new Map() };
      }
    }),
  );
  const vitalsByNetwork = new Map(results.map((x) => [x.networkId, x.byPool]));
  const nameByNetwork = new Map(results.map((x) => [x.networkId, x.networkName]));

  return list.map((r) => {
    const v = vitalsByNetwork.get(r.networkId)?.get(r.poolId) || {};
    return {
      networkId: r.networkId,
      poolId: r.poolId,
      // The ROOM's own name (pool name), NOT the composite slug — the tile pairs it
      // with the network name for the "Network name (room name)" label.
      name: v.name || r.poolId,
      // Friendly network name for the tile's primary "Network name (room name)" deck.
      networkName: nameByNetwork.get(r.networkId) || r.networkId,
      role: r.role || '',
      lifecycleStatus: r.lifecycleStatus || v.lifecycleStatus || '',
      lastActivityAt: r.lastActivityAt || v.lastActivityAt || null,
      releaseVersion: r.releaseVersion,
      stepflowVersion: v.stepflowVersion || '',
      // Vitals (absent → 0/null; the tile hides null metrics).
      runsTotal: Number(v.runsTotal || 0),
      runsSucceeded: Number(v.runsSucceeded || 0),
      successRatePct: v.successRatePct ?? null,
      consumedPoints: Number(v.consumedPoints || 0),
      consumedUsd: Number(v.consumedUsd || 0),
      outcomePct: v.outcomePct ?? null,
      isActive: Boolean(v.isActive),
      agentCount: Number(v.agentCount || 0),
    };
  });
}
